import { useCallback, useEffect, useRef } from 'react';

import type { AnalyticsEventPayload } from '@atlaskit/analytics-next/AnalyticsEvent';

import { ANALYTICS_SOURCE } from './analytics.ts';
import { getTwgTraceId } from './twgTraceId.ts';
import { useTwgAnalyticsEvents } from './useTwgAnalyticsEvents.ts';

/**
 * The Teamwork Graph adoption events, `twgResult viewed` and `twgResult actioned`, from the TWG
 * instrumentation contract: https://hello.atlassian.net/wiki/spaces/APD2/pages/7830489933
 *
 * Adoption MAU counts readers who were shown, or acted on, a non-empty TWG result, so neither
 * event ever fires for an empty result.
 */
export const TWG_RESULT_ACTION_SUBJECT = 'twgResult';

export type TwgResultAction = 'viewed' | 'actioned';

/** How the surface's data was queried from the graph, in the contract's terms. */
export type TwgQuerySource = 'AGS' | 'UGS' | 'Cypher' | 'DataDepot';

export type TwgQueryMethod = 'GraphQL' | 'REST';

/** A TWG-powered surface, described once and shared by its viewed and actioned events. */
export type TwgResultSurface = {
	/** The surface's existing id, so the TWG event joins that surface's own events. */
	actionSubjectId: string;
	/** The GASv3 `source`, the screen or surface the event came from. */
	source: string;
	/** The surface's entry in the TWG feature catalogue (TWGFC). */
	twgFeatureName: string;
	twgSource: TwgQuerySource;
	queryMethod: TwgQueryMethod;
};

/** Surface-specific attributes. The contract's base attributes win on a clash. */
export type TwgResultAttributes = Record<string, string | number | boolean | undefined>;

/**
 * Builds a `twgResult` event, or returns null when `twgResultCount` (how many TWG-sourced items the
 * surface rendered, the contract's non-empty proof) is not positive. `twgInteractionId` is the
 * `twgTraceId` that `useTwgAnalyticsEvents` also adds, so a view and its actions share one id.
 *
 * The contract names the query source `source`, which lives in `attributes` and is unrelated to
 * the GASv3 `source` on the event itself.
 */
export const getTwgResultEvent = (
	surface: TwgResultSurface,
	action: TwgResultAction,
	twgResultCount: number,
	attributes?: TwgResultAttributes,
): AnalyticsEventPayload | null => {
	if (twgResultCount <= 0) {
		return null;
	}
	return {
		type: 'sendUIEvent',
		data: {
			action,
			actionSubject: TWG_RESULT_ACTION_SUBJECT,
			actionSubjectId: surface.actionSubjectId,
			source: surface.source,
			attributes: {
				...attributes,
				product: 'confluence',
				subproduct: ANALYTICS_SOURCE,
				platform: 'web',
				eventname: `${TWG_RESULT_ACTION_SUBJECT} ${action}`,
				twgFeatureName: surface.twgFeatureName,
				twgInteractionId: getTwgTraceId(),
				source: surface.twgSource,
				query_method: surface.queryMethod,
				twgResultCount,
			},
		},
	};
};

/**
 * Fires `twgResult viewed` once per mount, the first time `twgResultCount` is positive. A surface
 * passes 0 until its result is on screen and settled, so "not ready" and "empty" are the same.
 */
export const useTwgResultViewed = (
	surface: TwgResultSurface,
	twgResultCount: number,
	attributes?: TwgResultAttributes,
): void => {
	const { createAnalyticsEvent } = useTwgAnalyticsEvents();
	const hasFiredRef = useRef(false);

	useEffect(() => {
		if (hasFiredRef.current) {
			return;
		}
		const event = getTwgResultEvent(surface, 'viewed', twgResultCount, attributes);
		if (event) {
			hasFiredRef.current = true;
			createAnalyticsEvent(event).fire();
		}
	}, [attributes, createAnalyticsEvent, surface, twgResultCount]);
};

/** A stable callback that fires `twgResult actioned`, and does nothing for an empty result. */
export const useTwgResultActioned = (
	surface: TwgResultSurface,
): ((twgResultCount: number, attributes?: TwgResultAttributes) => void) => {
	const { createAnalyticsEvent } = useTwgAnalyticsEvents();

	return useCallback(
		(twgResultCount: number, attributes?: TwgResultAttributes) => {
			const event = getTwgResultEvent(surface, 'actioned', twgResultCount, attributes);
			if (event) {
				createAnalyticsEvent(event).fire();
			}
		},
		[createAnalyticsEvent, surface],
	);
};
